import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  FileStack, Search, Loader2, Clock, Sparkles, Eye, Tag, Star, Trash2,
  History, FileDown, FileText as FileTextIcon, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  TEMPLATES, TEMPLATE_CATEGORIES,
  type LegalTemplate, type TemplateCategory,
} from "@/lib/templates/catalog";
import {
  listFavoriteSlugs, toggleFavorite, listUserTemplates, deleteUserTemplate,
  listGeneratedHistory,
} from "@/lib/templates.functions";
import { exportDraftPdf } from "@/lib/pdf.functions";
import { exportDraftDocx } from "@/lib/docx.functions";
import { listDraftRevisions, restoreDraftRevision } from "@/lib/drafting.functions";
import { TemplateEditor, type EditableTemplate } from "@/components/templates/template-editor";
import { useLocaleFmt } from "@/lib/i18n/format";

export const Route = createFileRoute("/_authenticated/app/templates")({
  component: TemplatesPage,
});

type Filter = TemplateCategory | "Tutti";

function TemplatesPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [filter, setFilter] = useState<Filter>("Tutti");
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<LegalTemplate | null>(null);
  const [editing, setEditing] = useState<EditableTemplate | null>(null);

  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [mine, setMine] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);

  const favFn = useServerFn(listFavoriteSlugs);
  const toggleFn = useServerFn(toggleFavorite);
  const mineFn = useServerFn(listUserTemplates);
  const delMineFn = useServerFn(deleteUserTemplate);
  const historyFn = useServerFn(listGeneratedHistory);

  const reloadFavorites = async () => {
    const r = await favFn({});
    if (r.ok) setFavorites(new Set(r.slugs));
  };
  const reloadMine = async () => {
    const r = await mineFn({});
    if (r.ok) setMine(r.items);
  };
  const reloadHistory = async () => {
    const r = await historyFn({});
    if (r.ok) setHistory(r.items);
  };

  useEffect(() => { void reloadFavorites(); void reloadMine(); void reloadHistory(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATES.filter((t) => {
      if (filter !== "Tutti" && t.category !== filter) return false;
      if (!q) return true;
      return t.name.toLowerCase().includes(q)
        || t.description.toLowerCase().includes(q)
        || t.tags.some((tag) => tag.toLowerCase().includes(q))
        || t.category.toLowerCase().includes(q);
    });
  }, [filter, query]);

  const favoriteTemplates = useMemo(
    () => TEMPLATES.filter((t) => favorites.has(t.slug)),
    [favorites],
  );

  const onToggleFav = async (slug: string) => {
    const r = await toggleFn({ data: { slug } });
    if (!r.ok) return toast.error(r.error ?? t("pages.templates.genericError"));
    setFavorites((prev) => {
      const next = new Set(prev);
      if (r.favorited) next.add(slug); else next.delete(slug);
      return next;
    });
  };

  const openEditorFromCatalog = (t: LegalTemplate) => {
    setPreview(null);
    setEditing({
      name: t.name, category: t.category, act_type: t.act_type,
      body: t.body, source_slug: t.slug,
    });
  };

  const openEditorFromMine = (t: any) => {
    setEditing({
      id: t.id, name: t.name, category: t.category ?? undefined,
      act_type: t.act_type ?? undefined, body: t.body, source_slug: t.source_slug ?? undefined,
    });
  };

  const removeMine = async (id: string) => {
    if (!confirm(t("pages.templates.deleteTpl"))) return;
    const r = await delMineFn({ data: { id } });
    if (!r.ok) return toast.error(r.error ?? t("pages.templates.genericError"));
    toast.success(t("pages.templates.deleted"));
    reloadMine();
  };

  return (
    <>
      <PageHeader
        title={t("pages.templates.title")}
        description={t("pages.templates.description")}
      />
      <PageBody>
        <Tabs defaultValue="catalog" className="space-y-5">
          <TabsList>
            <TabsTrigger value="catalog">{t("pages.templates.tabCatalog", { n: TEMPLATES.length })}</TabsTrigger>
            <TabsTrigger value="favorites">{t("pages.templates.tabFavorites", { n: favoriteTemplates.length })}</TabsTrigger>
            <TabsTrigger value="mine">{t("pages.templates.tabMine", { n: mine.length })}</TabsTrigger>
            <TabsTrigger value="history">{t("pages.templates.tabHistory")}</TabsTrigger>
          </TabsList>

          <TabsContent value="catalog">
            <div className="relative mb-6 overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 shadow-2xl backdrop-blur">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t("pages.templates.catalog")}</div>
                  <div className="mt-1 font-display text-2xl font-semibold text-foreground">
                    {t("pages.templates.catalogTitle", { n: TEMPLATES.length })}
                  </div>
                  <p className="mt-1 max-w-xl text-sm text-foreground">
                    {t("pages.templates.catalogDesc")}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-col gap-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("pages.templates.searchPh")}
                    className="border-border bg-secondary pl-9 text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div className="-mx-1 flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar">
                  {(["Tutti", ...TEMPLATE_CATEGORIES] as Filter[]).map((cat) => {
                    const active = filter === cat;
                    const count = cat === "Tutti" ? TEMPLATES.length : TEMPLATES.filter((tt) => tt.category === cat).length;
                    const label = cat === "Tutti" ? t("pages.templates.filterAll") : cat;
                    return (
                      <button
                        key={cat} type="button" onClick={() => setFilter(cat)}
                        className={cn(
                          "shrink-0 rounded-full px-3 py-1 text-[11px] font-medium transition-colors",
                          active ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary",
                        )}
                      >
                        {label}
                        <span className={cn("ml-1.5 text-[10px]", active ? "text-primary-foreground/80" : "text-muted-foreground")}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <CatalogGrid items={filtered} favorites={favorites} onPreview={setPreview}
              onUse={openEditorFromCatalog} onToggleFav={onToggleFav} />
          </TabsContent>

          <TabsContent value="favorites">
            {favoriteTemplates.length === 0 ? (
              <EmptyState text={t("pages.templates.noFavorites")} />
            ) : (
              <CatalogGrid items={favoriteTemplates} favorites={favorites} onPreview={setPreview}
                onUse={openEditorFromCatalog} onToggleFav={onToggleFav} />
            )}
          </TabsContent>

          <TabsContent value="mine">
            {mine.length === 0 ? (
              <EmptyState text={t("pages.templates.noMine")} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {mine.map((tpl) => (
                  <article key={tpl.id}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-secondary p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary text-foreground">
                        <FileStack className="h-5 w-5" />
                      </div>
                      <Button variant="ghost" size="sm" className="text-foreground hover:text-destructive"
                        onClick={() => removeMine(tpl.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {tpl.category ?? t("pages.templates.personal")}
                    </div>
                    <h3 className="mt-1 font-display text-base font-semibold text-foreground">{tpl.name}</h3>
                    <p className="mt-1 line-clamp-2 text-[13px] text-foreground">
                      {(tpl.body ?? "").slice(0, 120)}…
                    </p>
                    <div className="mt-4">
                      <Button size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={() => openEditorFromMine(tpl)}>{t("pages.templates.open")}</Button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <HistoryPanel items={history} reload={reloadHistory} />
          </TabsContent>
        </Tabs>
      </PageBody>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-h-[85vh] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{preview?.name}</DialogTitle>
            <DialogDescription>
              {preview?.category} · {preview?.est_minutes} min ·{" "}
              {preview?.complexity === "avanzato" ? t("pages.templates.advanced") : t("pages.templates.basic")}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <>
              <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap rounded-lg border bg-muted/40 p-4 font-mono text-xs leading-relaxed">
                {preview.body}
              </pre>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPreview(null)}>{t("pages.templates.close")}</Button>
                <Button onClick={() => preview && openEditorFromCatalog(preview)}>
                  {t("pages.templates.use")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <TemplateEditor open={!!editing} template={editing}
        onClose={() => setEditing(null)} onSaved={reloadMine} />
    </>
  );
}

function CatalogGrid({
  items, favorites, onPreview, onUse, onToggleFav,
}: {
  items: LegalTemplate[];
  favorites: Set<string>;
  onPreview: (t: LegalTemplate) => void;
  onUse: (t: LegalTemplate) => void;
  onToggleFav: (slug: string) => void;
}) {
  const { t: tr } = useTranslation();
  if (items.length === 0) {
    return <EmptyState text={tr("pages.templates.noMatch")} />;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((t) => {
        const fav = favorites.has(t.slug);
        return (
          <article key={t.slug}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-5 transition-all hover:border-border">
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary text-foreground">
                <FileStack className="h-5 w-5" />
              </div>
              <div className="flex items-center gap-1.5">
                {t.complexity === "avanzato" && (
                  <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-300">
                    <Sparkles className="mr-1 inline h-2.5 w-2.5" />{tr("pages.templates.advanced")}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] text-foreground">
                  <Clock className="h-3 w-3" />{t.est_minutes}′
                </span>
                <button type="button" onClick={() => onToggleFav(t.slug)}
                  className={cn("rounded-full p-1.5 transition", fav ? "text-amber-300" : "text-muted-foreground hover:text-foreground")}>
                  <Star className={cn("h-4 w-4", fav && "fill-current")} />
                </button>
              </div>
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t.category}</div>
            <h3 className="mt-1 font-display text-base font-semibold text-foreground">{t.name}</h3>
            <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-foreground">{t.description}</p>
            {t.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {t.tags.slice(0, 3).map((tag) => (
                  <span key={tag}
                    className="inline-flex items-center gap-1 rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    <Tag className="h-2.5 w-2.5" />{tag}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-5 flex gap-2">
              <Button size="sm" variant="outline"
                className="flex-1 border-border bg-secondary text-foreground hover:bg-secondary"
                onClick={() => onPreview(t)}>
                <Eye className="mr-1.5 h-3.5 w-3.5" />{tr("pages.templates.preview")}
              </Button>
              <Button size="sm" className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => onUse(t)}>{tr("pages.templates.use")}</Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-border bg-secondary p-10 text-center text-sm text-foreground">
      {text}
    </div>
  );
}

function HistoryPanel({ items, reload }: { items: any[]; reload: () => void }) {
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const exportPdfFn = useServerFn(exportDraftPdf);
  const exportDocxFn = useServerFn(exportDraftDocx);
  const listRevFn = useServerFn(listDraftRevisions);
  const restoreFn = useServerFn(restoreDraftRevision);
  const [busy, setBusy] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<{ draftId: string; items: any[] } | null>(null);

  const dl = (filename: string, base64: string, mime: string) => {
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let j = 0; j < bin.length; j++) arr[j] = bin.charCodeAt(j);
    const url = URL.createObjectURL(new Blob([arr], { type: mime }));
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const exportAs = async (id: string, kind: "pdf" | "docx") => {
    setBusy(`${id}:${kind}`);
    try {
      const r = kind === "pdf"
        ? await exportPdfFn({ data: { draftId: id } })
        : await exportDocxFn({ data: { draftId: id } });
      dl(r.filename, r.base64, r.mimeType);
    } catch (e: any) { toast.error(e?.message ?? tr("pages.templates.genericError")); }
    finally { setBusy(null); }
  };

  const openRevisions = async (id: string) => {
    setBusy(`${id}:rev`);
    const r = await listRevFn({ data: { draftId: id } });
    setBusy(null);
    if (!r.ok) return toast.error(r.error ?? tr("pages.templates.genericError"));
    setRevisions({ draftId: id, items: r.items });
  };

  const restore = async (revisionId: string) => {
    const r = await restoreFn({ data: { revisionId } });
    if (!r.ok) return toast.error(r.error ?? tr("pages.templates.genericError"));
    toast.success(tr("pages.templates.restored"));
    setRevisions(null);
    reload();
  };

  if (items.length === 0) {
    return <EmptyState text={tr("pages.templates.noHistory")} />;
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-border bg-secondary">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">{tr("pages.templates.colDoc")}</th>
              <th className="px-4 py-3">{tr("pages.templates.colType")}</th>
              <th className="px-4 py-3">{tr("pages.templates.colUpdated")}</th>
              <th className="px-4 py-3 text-right">{tr("pages.templates.colActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((d) => (
              <tr key={d.id} className="hover:bg-secondary">
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{d.title}</div>
                  <div className="text-xs text-muted-foreground">{Math.round(d.size / 1024)} KB</div>
                </td>
                <td className="px-4 py-3 text-foreground">{d.act_type ?? "-"}</td>
                <td className="px-4 py-3 text-foreground">
                  {new Date(d.updated_at).toLocaleString(localeTag, { dateStyle: "short", timeStyle: "short" })}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => navigate({ to: "/app/drafting", search: { d: d.id } as any })}>
                      {tr("pages.templates.open")}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === `${d.id}:pdf`} onClick={() => exportAs(d.id, "pdf")}>
                      {busy === `${d.id}:pdf` ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
                      <span className="ml-1">{tr("pages.templates.pdf")}</span>
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === `${d.id}:docx`} onClick={() => exportAs(d.id, "docx")}>
                      {busy === `${d.id}:docx` ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileTextIcon className="h-3 w-3" />}
                      <span className="ml-1">{tr("pages.templates.docx")}</span>
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === `${d.id}:rev`} onClick={() => openRevisions(d.id)}>
                      <History className="h-3 w-3" /> <span className="ml-1">{tr("pages.templates.versions")}</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!revisions} onOpenChange={(v) => !v && setRevisions(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{tr("pages.templates.versionsTitle")}</DialogTitle>
            <DialogDescription>{tr("pages.templates.versionsDesc")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {revisions?.items.length === 0 && (
              <p className="text-sm text-muted-foreground">{tr("pages.templates.noVersions")}</p>
            )}
            {revisions?.items.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="text-sm font-medium">{r.label ?? tr("pages.templates.version")}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString(localeTag)}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(r.id)}>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> {tr("pages.templates.restore")}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
